import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from "lucide-react";
import { useRoom } from "../context/RoomContext";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 2;

const HotelAdd_RoomInfo_3 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { updateRoomStep, fetchRoom, roomData, loading } = useRoom();
  const { hotelId, roomId } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const [isSaving, setIsSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const manage = searchParams.get('manage') || '';


  const [promotionChoice, setPromotionChoice] = useState("");
  const [earlyBirdDiscount, setEarlyBirdDiscount] = useState("");
  const [earlyBirdDiscountDays, setEarlyBirdDiscountDays] = useState("");
  const [addonDiscount, setAddonDiscount] = useState("");
  const [customPromotionsDiscount, setCustomPromotionsDiscount] = useState("");
  const [customPromotionsLabel, setCustomPromotionsLabel] = useState("");

  useEffect(() => {
    if (roomId) {
      fetchRoom(roomId);
    }
  }, [roomId, fetchRoom]);


  useEffect(() => {
    if (roomData?.information3) {
      const { promotionChoice, earlyBirdDiscount, earlyBirdDiscountDays, addonDiscount, customPromotionsDiscount, customPromotionsLabel } = roomData.information3;
      setPromotionChoice(promotionChoice || "");
      setEarlyBirdDiscount(earlyBirdDiscount || "");
      setEarlyBirdDiscountDays(earlyBirdDiscountDays || "");
      setAddonDiscount(addonDiscount || "");
      setCustomPromotionsDiscount(customPromotionsDiscount || "");
      setCustomPromotionsLabel(customPromotionsLabel || "");
    }
  }, [roomData?.information3]);

  const handleNext = async () => {
    try {
      setIsSaving(true);

      // Build promotions payload based on promotionChoice
      const information3 = {
        promotionChoice: promotionChoice,
      };

      if (promotionChoice === "earlyBird") {
        information3.earlyBirdDiscount = Number(earlyBirdDiscount);
        information3.earlyBirdDiscountDays = Number(earlyBirdDiscountDays);
      } else if (promotionChoice === "addon") {
        information3.addonDiscount = Number(addonDiscount);
      } else if (promotionChoice === "custom") {
        information3.customPromotionsDiscount = Number(customPromotionsDiscount);
        information3.customPromotionsLabel = customPromotionsLabel;
      }

      await updateRoomStep(roomId, "information3", { information3 });

      if (manage) {
        navigate(`/hotel/${hotelId}/room-overview/${roomId}?manage=true`);
      } else {
        navigate(`/hotel/${hotelId}/room-overview/${roomId}`);
      }
    } catch (err) {
      console.error("Error saving promotions:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      setIsSaving(true);

      // Build promotions payload based on promotionChoice
      const information3 = {
        promotionChoice: promotionChoice,
      };

      if (promotionChoice === "earlyBird") {
        information3.earlyBirdDiscount = Number(earlyBirdDiscount);
        information3.earlyBirdDiscountDays = Number(earlyBirdDiscountDays);
      } else if (promotionChoice === "addon") {
        information3.addonDiscount = Number(addonDiscount);
      } else if (promotionChoice === "custom") {
        information3.customPromotionsDiscount = Number(customPromotionsDiscount);
        information3.customPromotionsLabel = customPromotionsLabel;
      }

      await updateRoomStep(roomId, "information3", { information3 });

      if (manage) {
        navigate(`/hotel/room-management/${hotelId}`);
      } else {
        navigate(`/hotel/onboarding`);
      }
    } catch (err) {
      console.error("Error saving promotions:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const discountFieldSx = {
    width: isMobile ? "70px" : "90px",
    minWidth: isMobile ? "70px" : "90px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px",
    },
  };

  const customLabelFieldSx = {
    width: isMobile ? "180px" : "350px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px",
    },
  };


  const isNextDisabled =
    !promotionChoice ||
    (promotionChoice === "earlyBird" && !earlyBirdDiscount) ||
    (promotionChoice === "earlyBird" && !earlyBirdDiscountDays) ||
    (promotionChoice === "addon" && !addonDiscount) ||
    (promotionChoice === "custom" && !customPromotionsDiscount) ||
    (promotionChoice === "custom" && !customPromotionsLabel);


  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto", gap: manage && 1.5,  width:  manage && isMobile ? "100%" : manage ? "60%" : "auto", }}>
          {manage ? (
            <>
              <Button
                onClick={() => navigate(`/hotel/${hotelId}/room-info-2/${roomId}?manage=true`)}
                sx={{
                  minWidth: "auto",
                  width: isMobile ? "36px" : "44px",
                  height: isMobile ? "36px" : "44px",
                  borderRadius: "50%",
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  padding: 0,
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                }}
              >
                <ArrowLeft size={isMobile ? 18 : 22} />
              </Button>
              <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
                {t('RoomManagementTitle')} {t('AddaNewRoomTitle')}
              </Typography>
            </>
          ) : (
            STEPS.map((step, index) => (
              <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: isMobile ? "20px" : "30px",
                      height: isMobile ? "20px" : "30px",
                      borderRadius: "50%",
                      backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                      border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                      boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: isMobile ? "10px" : "13px",
                      fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                      color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                      textAlign: "center",
                      whiteSpace: "wrap",
                      width: isMobile ? "50px" : "100px",
                    }}
                  >
                    {t(step.labelKey)}
                  </Typography>
                </Box>
                {index < STEPS.length - 1 && (
                  <Box
                    sx={{
                      height: "1px",
                      width: isMobile ? "30px" : "200px",
                      backgroundColor: "#D1D1D1",
                      mx: "auto",
                      mb: isMobile ? "18px" : "24px",
                    }}
                  />
                )}
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 4, display: "flex", alignItems: "center", gap: 1 }}>
            {t('RoomInfoStep3Title')}
            <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
              {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
            </Box>
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
              <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                {t('PromotionQuestionLabel')} <span style={{ color: "red" }}> *</span>
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, cursor: "pointer" }} onClick={() => setPromotionChoice("none")}>
                  <Checkbox
                    checked={promotionChoice === "none"}
                    onChange={() => setPromotionChoice("none")}
                    sx={{
                      borderRadius: "6px",
                      "&.Mui-checked": { color: "#31BACD" },
                    }}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                    {t('NoPromotionLabel')}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
                  <Checkbox
                    checked={promotionChoice === "earlyBird"}
                    onChange={() => setPromotionChoice("earlyBird")}
                    sx={{
                      borderRadius: "6px",
                      "&.Mui-checked": { color: "#31BACD" },
                    }}
                  />
                  <TextField
                    type="number"
                    value={earlyBirdDiscount}
                    onChange={(e) => setEarlyBirdDiscount(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                    {t('DiscountLabel')}
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#000" }}>
                    {t('EarlyBirdPromotionLabel1')}
                  </Typography>
                  <TextField
                    type="number"
                    value={earlyBirdDiscountDays}
                    onChange={(e) => setEarlyBirdDiscountDays(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#000" }}>
                    {t('EarlyBirdPromotionLabel2')}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
                  <Checkbox
                    checked={promotionChoice === "addon"}
                    onChange={() => setPromotionChoice("addon")}
                    sx={{
                      borderRadius: "6px",
                      "&.Mui-checked": { color: "#31BACD" },
                    }}
                  />
                  <TextField
                    type="number"
                    value={addonDiscount}
                    onChange={(e) => setAddonDiscount(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                    {t('DiscountLabel')}
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#000" }}>
                    {t('AddonPromotionLabel')}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
                  <Checkbox
                    checked={promotionChoice === "custom"}
                    onChange={() => setPromotionChoice("custom")}
                    sx={{
                      borderRadius: "6px",
                      "&.Mui-checked": { color: "#31BACD" },
                    }}
                  />
                  <TextField
                    type="number"
                    value={customPromotionsDiscount || ""}
                    onChange={(e) => setCustomPromotionsDiscount(e.target.value)}
                    sx={discountFieldSx}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", }}>
                    {t('DiscountLabel')}
                  </Typography>
                  <TextField
                    placeholder={t('CustomPromotionPlaceholder')}
                    value={customPromotionsLabel || ""}
                    onChange={(e) => setCustomPromotionsLabel(e.target.value)}
                    sx={customLabelFieldSx}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(manage ? `/hotel/${hotelId}/room-info-2/${roomId}?manage=true` : `/hotel/${hotelId}/room-info-2/${roomId}`)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_RoomInfo_3;
